// ────────────────────────────────────────────────────────────────────────────
// Dex live-scan debug-overlay flag (b13c).
//
// When ON, the live-scan hook appends a diagnostic sub-line to the PiP pill's
// `detail` field showing the last-frame timestamp and the last OCR result
// (region / mode / numbersRead). This lets Sam diagnose the broadcast → OCR
// handoff visually on device — without an Xcode debugger and without any
// UI/UX styling change (it reuses the existing text-only `detail` line).
//
// Default OFF: this is a diagnostic surface, not a shipping feature. The
// b13a→b13c break (the pre-OCR blur gate silently dropping every broadcast
// frame so framesObserved stayed 0 and the pill never advanced) was invisible
// on device precisely because nothing surfaced "frames arrived but were
// dropped". This flag makes that visible.
//
// Resolution order (first ON wins):
//
//   1. Build-time env var `VITE_DEX_SCAN_DEBUG_OVERLAY=1` (or "true").
//      Bake the overlay into a specific diagnostic TestFlight build.
//
//   2. `localStorage["dex.scan.debugOverlay"] === "1"`.
//      Sticky device override, set via Safari Web Inspector mid-session.
//
//   3. URL hash `#dex-scan-debug`.
//      One-shot device probe; promoted to localStorage so reloads stay on.
//
//   4. Otherwise: OFF.
// ────────────────────────────────────────────────────────────────────────────

export const DEX_SCAN_DEBUG_OVERLAY_LS_KEY = "dex.scan.debugOverlay";
export const DEX_SCAN_DEBUG_OVERLAY_URL_HASH = "dex-scan-debug";

let cached: boolean | null = null;

/** Return true when an explicit ON override is set. */
function readEnabledOverride(): boolean {
  // Env: only "1"/"true" enables. Other values keep default-off.
  try {
    const raw = (import.meta as unknown as {
      env?: Record<string, string | undefined>;
    }).env?.VITE_DEX_SCAN_DEBUG_OVERLAY;
    if (raw === "1" || raw === "true") return true;
  } catch {
    // ignore
  }
  if (typeof window === "undefined") return false;
  // LocalStorage explicit "1" enables.
  try {
    if (window.localStorage.getItem(DEX_SCAN_DEBUG_OVERLAY_LS_KEY) === "1") return true;
  } catch {
    // ignore
  }
  // URL hash on-probe; promote to LS so subsequent reloads stay on.
  const hash = window.location?.hash?.toLowerCase() ?? "";
  if (hash.includes(DEX_SCAN_DEBUG_OVERLAY_URL_HASH)) {
    try {
      window.localStorage.setItem(DEX_SCAN_DEBUG_OVERLAY_LS_KEY, "1");
    } catch {
      // ignore
    }
    return true;
  }
  return false;
}

/**
 * Returns true when the live-scan debug overlay should append a diagnostic
 * sub-line to the PiP pill. Resolves at most once per page-load and caches
 * the result. Default OFF — see file header for on-override mechanisms.
 */
export function isDexScanDebugOverlayEnabled(): boolean {
  if (cached !== null) return cached;
  cached = readEnabledOverride();
  return cached;
}

/** Test-only escape hatch. */
export function __resetDexScanDebugOverlayCache(): void {
  cached = null;
}
